import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  LOST_REASONS,
  PAYMENT_METHODS,
  PLANS,
  PRODUCTS,
} from "./gulaConstants";
import { CASH_METHOD, isInCatalonia, paymentOptionsFor } from "./cashPayment";
import { fullName, type GulaClient } from "./gulaTypes";
import { Field, SimpleSelect } from "./SimpleSelect";
import { useAgents } from "./useAgents";

export type PendingMove = { client: GulaClient; toStage: string } | null;

const inAWeek = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
};

/** Note saved when a lead goes to "Compra segura" (shown on the card). */
const closingNoteText = (note: string) => `Nota de cierre: ${note.trim()}`;

/**
 * Asks for the extra data some moves need: a date to call back, the closing
 * note of a sure purchase, the reason a lead did not buy, or the order details
 * when the lead buys.
 */
export const StageChangeDialog = ({
  pending,
  onClose,
  onDone,
}: {
  pending: PendingMove;
  onClose: () => void;
  onDone: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { meId } = useAgents();
  const [saving, setSaving] = useState(false);
  const [closingNote, setClosingNote] = useState("");
  const [recontactAt, setRecontactAt] = useState(inAWeek());
  const [lostReason, setLostReason] = useState<string>("price");
  const [product, setProduct] = useState(PRODUCTS[0].value);
  const [plan, setPlan] = useState("none");
  const [price, setPrice] = useState(String(PRODUCTS[0].price));
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isPaid, setIsPaid] = useState(true);
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [notes, setNotes] = useState("");

  const client = pending?.client;
  useEffect(() => {
    if (!client) return;
    setAddress(client.address ?? "");
    setPostalCode(client.postal_code ?? "");
    setCity(client.city ?? "");
    setProvince(client.province ?? "");
    setNotes("");
    setClosingNote("");
  }, [client]);

  if (!pending || !client) return null;
  const { toStage } = pending;
  const inCatalonia = isInCatalonia(postalCode, client.zone);
  const isCash = paymentMethod === CASH_METHOD;

  const choosePaymentMethod = (method: string) => {
    setPaymentMethod(method);
    // Cash is collected on installation day, not when the sale is made
    if (method === CASH_METHOD) setIsPaid(false);
  };

  const updateClient = (data: Record<string, unknown>) =>
    dataProvider.update("gula_clients", {
      id: client.id,
      data,
      previousData: { id: client.id },
    });

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (toStage === "recontact") {
        await updateClient({ stage: toStage, recontact_at: recontactAt });
      } else if (toStage === "sure_purchase") {
        if (!closingNote.trim()) {
          notify("Escribe la nota de cierre", { type: "warning" });
          setSaving(false);
          return;
        }
        await updateClient({ stage: toStage, lost_reason: null });
        await dataProvider.create("contact_notes", {
          data: {
            contact_id: client.contact_id,
            text: closingNoteText(closingNote),
            date: new Date().toISOString(),
            sales_id: meId ?? null,
          },
        });
        notify(`${client.first_name ?? "Lead"} está en Compra segura`, {
          type: "success",
        });
      } else if (toStage === "no_purchase") {
        await updateClient({ stage: toStage, lost_reason: lostReason });
      } else if (toStage === "purchased") {
        if (!address || !postalCode || !city) {
          notify("Falta la dirección completa para preparar el envío", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        if (isCash && !inCatalonia) {
          notify("El pago en efectivo solo se puede hacer en Cataluña", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        await updateClient({
          stage: toStage,
          address,
          postal_code: postalCode,
          city,
          province,
          lost_reason: null,
        });
        const { data: fresh } = await dataProvider.getOne("gula_clients", {
          id: client.id,
        });
        await dataProvider.create("gula_orders", {
          data: {
            client_id: client.id,
            contact_id: client.contact_id,
            stage: "to_ship",
            product,
            plan,
            price: Number(price) || null,
            payment_method: paymentMethod,
            paid_at: isPaid ? new Date().toISOString() : null,
            installer_id: fresh?.installer_id ?? null,
            notes: notes || null,
          },
        });
        notify("Venta registrada: el pedido ya está en Operaciones", {
          type: "success",
        });
      }
      onDone();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const title =
    toStage === "recontact"
      ? "¿Cuándo hay que recontactar?"
      : toStage === "sure_purchase"
        ? "Compra segura: nota de cierre"
        : toStage === "no_purchase"
          ? "¿Por qué no compra?"
          : "Registrar la venta";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{fullName(client)}</DialogDescription>
        </DialogHeader>

        {toStage === "recontact" ? (
          <Field label="Fecha para volver a llamar">
            <Input
              type="date"
              value={recontactAt}
              onChange={(event) => setRecontactAt(event.target.value)}
            />
          </Field>
        ) : null}

        {toStage === "sure_purchase" ? (
          <Field label="Nota de cierre">
            <Textarea
              autoFocus
              rows={4}
              value={closingNote}
              onChange={(event) => setClosingNote(event.target.value)}
              placeholder="Qué equipo quiere, cómo va a pagar, cuándo lo cierra…"
            />
          </Field>
        ) : null}

        {toStage === "no_purchase" ? (
          <SimpleSelect
            label="Motivo"
            value={lostReason}
            onChange={setLostReason}
            options={LOST_REASONS}
          />
        ) : null}

        {toStage === "purchased" ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <SimpleSelect
                label="Equipo"
                value={product}
                onChange={(value) => {
                  setProduct(value);
                  const match = PRODUCTS.find((item) => item.value === value);
                  if (match) setPrice(String(match.price));
                }}
                options={PRODUCTS}
              />
            </div>
            <SimpleSelect
              label="Plan de mantenimiento"
              value={plan}
              onChange={setPlan}
              options={PLANS}
            />
            <Field label="Precio (€)">
              <Input
                type="number"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
            </Field>
            <div className="col-span-2">
              <SimpleSelect
                label="Forma de pago"
                value={paymentMethod}
                onChange={choosePaymentMethod}
                options={paymentOptionsFor(
                  PAYMENT_METHODS,
                  inCatalonia || isCash,
                )}
              />
              {isCash ? (
                <p
                  className={`mt-1 text-xs ${
                    inCatalonia
                      ? "text-muted-foreground"
                      : "font-medium text-destructive"
                  }`}
                >
                  {inCatalonia
                    ? "Solo Cataluña: se cobra en mano el día de la instalación."
                    : "El efectivo solo vale en Cataluña: elige otra forma de pago o revisa el código postal."}
                </p>
              ) : null}
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox
                id="is-paid"
                checked={isPaid}
                onCheckedChange={(checked) => setIsPaid(checked === true)}
              />
              <Label htmlFor="is-paid">Pago recibido</Label>
            </div>
            <div className="col-span-2">
              <Field label="Dirección de envío e instalación">
                <Input
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="Calle, número, piso"
                />
              </Field>
            </div>
            <Field label="Código postal">
              <Input
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
              />
            </Field>
            <Field label="Población">
              <Input
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </Field>
            <div className="col-span-2">
              <Field label="Provincia">
                <Input
                  value={province}
                  onChange={(event) => setProvince(event.target.value)}
                />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Notas para el envío o la instalación">
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </Field>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Guardando…" : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
